import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { DeckElement, DeckSlide, DeckTheme } from '@/lib/slideDeck';
import { clamp } from '@/lib/slideDeck';
import { elementInnerStyle } from '@/components/feature/presentation/DeckSlideStatic';

interface SlideCanvasProps {
  slide: DeckSlide;
  theme: DeckTheme;
  selectedId: string | null;
  readOnly?: boolean;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, patch: Partial<DeckElement>) => void;
  onBeginEdit: () => void;
  onRemove: (id: string) => void;
}

type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLES: { dir: Handle; className: string; cursor: string }[] = [
  { dir: 'nw', className: 'left-0 top-0 -translate-x-1/2 -translate-y-1/2', cursor: 'nwse-resize' },
  { dir: 'n', className: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2', cursor: 'ns-resize' },
  { dir: 'ne', className: 'right-0 top-0 translate-x-1/2 -translate-y-1/2', cursor: 'nesw-resize' },
  { dir: 'e', className: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2', cursor: 'ew-resize' },
  { dir: 'se', className: 'right-0 bottom-0 translate-x-1/2 translate-y-1/2', cursor: 'nwse-resize' },
  { dir: 's', className: 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2', cursor: 'ns-resize' },
  { dir: 'sw', className: 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2', cursor: 'nesw-resize' },
  { dir: 'w', className: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2', cursor: 'ew-resize' },
];

function ChartBlock({ el, theme }: { el: DeckElement; theme: DeckTheme }) {
  const chart = el.chart;
  if (!chart) return null;
  const max = Math.max(...chart.values, 1);
  if (chart.kind === 'pie') {
    const total = chart.values.reduce((a, b) => a + b, 0) || 1;
    let acc = 0;
    const palette = ['#0f766e', '#c2410c', '#a16207', '#4d7c0f', '#9d174d', '#1e40af'];
    const stops = chart.values
      .map((v, i) => {
        const start = (acc / total) * 100;
        acc += v;
        return `${palette[i % palette.length]} ${start}% ${(acc / total) * 100}%`;
      })
      .join(', ');
    return <div className="w-full h-full rounded-full" style={{ background: `conic-gradient(${stops})` }} />;
  }
  return (
    <div className="w-full h-full flex items-end gap-[2%] pb-[8%]">
      {chart.values.slice(0, 14).map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
          <div
            className="w-full rounded-t"
            style={{ height: `${Math.max(3, (v / max) * 100)}%`, background: el.fill || theme.primary, opacity: 0.9 }}
            title={`${chart.categories[i] ?? i} : ${v}`}
          />
        </div>
      ))}
    </div>
  );
}

export default function SlideCanvas({
  slide,
  theme,
  selectedId,
  readOnly = false,
  onSelect,
  onUpdate,
  onBeginEdit,
  onRemove,
}: SlideCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ mode: 'move' | Handle; startX: number; startY: number; el: DeckElement } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!drag || !rect) return;
      const dx = ((e.clientX - drag.startX) / rect.width) * 100;
      const dy = ((e.clientY - drag.startY) / rect.height) * 100;
      const el = drag.el;
      if (drag.mode === 'move') {
        onUpdate(el.id, {
          x: clamp(el.x + dx, -el.w + 4, 100 - 4),
          y: clamp(el.y + dy, -el.h + 4, 100 - 4),
        });
        return;
      }
      let { x, y, w, h } = el;
      if (drag.mode.includes('e')) w = clamp(el.w + dx, 3, 100 - el.x);
      if (drag.mode.includes('s')) h = clamp(el.h + dy, 3, 100 - el.y);
      if (drag.mode.includes('w')) {
        const nx = clamp(el.x + dx, 0, el.x + el.w - 3);
        w = el.w + (el.x - nx);
        x = nx;
      }
      if (drag.mode.includes('n')) {
        const ny = clamp(el.y + dy, 0, el.y + el.h - 3);
        h = el.h + (el.y - ny);
        y = ny;
      }
      onUpdate(el.id, { x, y, w, h });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onUpdate]);

  const startDrag = (e: ReactMouseEvent, el: DeckElement, mode: 'move' | Handle) => {
    if (readOnly || editingId) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(el.id);
    onBeginEdit();
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, el };
  };

  return (
    <div
      ref={canvasRef}
      onMouseDown={() => {
        onSelect(null);
        setEditingId(null);
      }}
      className="relative w-full overflow-hidden rounded-lg border border-background-200/70"
      style={{
        aspectRatio: '16 / 9',
        backgroundColor: slide.bg || theme.background,
        backgroundImage: slide.bgImage ? `url(${slide.bgImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        containerType: 'inline-size',
        fontFamily: theme.bodyFont,
      }}
    >
      {slide.elements.map((el) => {
        const selected = el.id === selectedId;
        const base = elementInnerStyle(el);
        const isEditing = editingId === el.id;
        const textLike = el.type === 'text' || el.type === 'wordart';

        let content: React.ReactNode = null;
        if (el.type === 'image') {
          content = <img src={el.src} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />;
        } else if (el.type === 'shape') {
          content = (
            <div
              style={{
                width: '100%',
                height: '100%',
                background: el.fill || theme.primary,
                borderRadius: el.shape === 'ellipse' ? '50%' : el.shape === 'roundRect' ? `${el.radius ?? 12}px` : undefined,
                clipPath:
                  el.shape === 'triangle'
                    ? 'polygon(50% 0%, 100% 100%, 0% 100%)'
                    : el.shape === 'diamond'
                      ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
                      : el.shape === 'arrow'
                        ? 'polygon(0% 35%, 62% 35%, 62% 0%, 100% 50%, 62% 100%, 62% 65%, 0% 65%)'
                        : undefined,
                border: el.stroke ? `${el.strokeWidth || 1}px solid ${el.stroke}` : undefined,
              }}
            />
          );
        } else if (el.type === 'line') {
          content = <div style={{ width: '100%', height: `${el.strokeWidth || 2}px`, background: el.stroke || theme.text, marginTop: 'calc(50% - 1px)' }} />;
        } else if (el.type === 'chart') {
          content = <ChartBlock el={el} theme={theme} />;
        } else if (el.type === 'table' && el.table) {
          content = (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '2cqw', color: el.color || theme.text }}>
              <thead>
                <tr>
                  {el.table.headers.map((h, i) => (
                    <th key={i} style={{ border: '1px solid rgba(120,120,120,0.35)', padding: '0.7cqw 1cqw', background: 'rgba(15,118,110,0.12)', textAlign: 'left' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {el.table.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((c, ci) => (
                      <td key={ci} style={{ border: '1px solid rgba(120,120,120,0.28)', padding: '0.6cqw 1cqw' }}>
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        } else if (el.type === 'video') {
          content = (
            <div className="w-full h-full bg-foreground-900 text-background-50 flex flex-col items-center justify-center gap-1">
              <i className="ri-play-circle-line" style={{ fontSize: '5cqw' }}></i>
              <span style={{ fontSize: '1.6cqw' }} className="truncate px-2 max-w-full">{el.videoUrl}</span>
            </div>
          );
        } else {
          const textStyle: React.CSSProperties = {
            fontSize: `${el.fontSize ?? 3}cqw`,
            fontFamily: el.type === 'wordart' ? theme.headingFont : theme.bodyFont,
            color: el.color || theme.text,
            fontWeight: el.bold || el.type === 'wordart' ? 700 : 400,
            fontStyle: el.italic ? 'italic' : undefined,
            textDecoration: el.underline ? 'underline' : undefined,
            lineHeight: 1.25,
            wordBreak: 'break-word',
            outline: 'none',
          };
          content = isEditing ? (
            <div
              contentEditable
              suppressContentEditableWarning
              autoFocus
              onBlur={(ev) => {
                onUpdate(el.id, { html: ev.currentTarget.innerHTML });
                setEditingId(null);
              }}
              onKeyDown={(ev) => {
                if (ev.key === 'Escape') {
                  ev.currentTarget.blur();
                }
              }}
              style={textStyle}
              dangerouslySetInnerHTML={{ __html: el.html || '' }}
            />
          ) : (
            <div style={textStyle} dangerouslySetInnerHTML={{ __html: el.html || '<span style="opacity:.45">Texte</span>' }} />
          );
        }

        return (
          <div
            key={el.id}
            onMouseDown={(e) => startDrag(e, el, 'move')}
            onDoubleClick={(e) => {
              if (readOnly || !textLike) return;
              e.stopPropagation();
              onBeginEdit();
              setEditingId(el.id);
            }}
            onMouseEnter={() => setHoverId(el.id)}
            onMouseLeave={() => setHoverId(null)}
            style={{
              ...base,
              cursor: readOnly ? 'default' : 'move',
              display: el.type === 'line' ? 'block' : 'flex',
              flexDirection: 'column',
              justifyContent: el.valign === 'middle' ? 'center' : el.valign === 'bottom' ? 'flex-end' : 'flex-start',
              textAlign: el.align || 'left',
              padding: textLike ? '0.6cqw 0.8cqw' : 0,
              boxShadow: el.shadow ? '0 8px 24px rgba(15,23,42,0.18)' : undefined,
            }}
          >
            {content}
            {!readOnly && (selected || hoverId === el.id) && !isEditing && (
              <div
                className="pointer-events-none absolute inset-0"
                style={{ border: `1.5px solid ${selected ? theme.primary : 'rgba(15,118,110,0.45)'}` }}
              />
            )}
            {!readOnly && selected && !isEditing && (
              <>
                {HANDLES.map((hnd) => (
                  <span
                    key={hnd.dir}
                    onMouseDown={(e) => startDrag(e, el, hnd.dir)}
                    className={`absolute w-2.5 h-2.5 rounded-full bg-white border border-primary-500 ${hnd.className}`}
                    style={{ cursor: hnd.cursor }}
                  />
                ))}
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(el.id);
                  }}
                  className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-white border border-red-300 text-red-600 flex items-center justify-center cursor-pointer hover:bg-red-50"
                  title="Supprimer l'élément"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}